// Full-page comparison of the five delapresse plans.
// Same matrix as PricingPage's section D, presented stand-alone with:
//   - Print button (window.print(); CSS hides nav/footer for print)
//   - Email-this-comparison form (POSTs to marketing-send-comparison
//     edge function, which renders the HTML and ships via Resend)

import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, X, Printer, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { buildBreadcrumbJsonLd, buildOrganizationJsonLd } from "@/lib/seo";
import {
  PLAN_DISPLAY_NAMES,
  PLAN_MATRIX,
  PLAN_SLUGS,
  formatCell,
  locText,
} from "@/lib/marketing-plan-matrix";
import { useLocale, type Locale } from "@/lib/locale";
import { formatMoney, planMonthlyCents } from "@/lib/currency";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function formatPriceOrCustom(cents: number | null, locale: Locale): string {
  if (cents === null) return locale === "fr" ? "Sur devis" : "On request";
  return formatMoney(cents, locale);
}

export default function PricingComparisonPage() {
  const { t, i18n } = useTranslation();
  const locale = useLocale();

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "ok">("idle");

  const jsonLd = [
    buildOrganizationJsonLd(),
    buildBreadcrumbJsonLd([
      { name: t("marketing.breadcrumbs.home"), path: "/" },
      { name: t("marketing.header.nav.pricing"), path: "/tarifs" },
      { name: t("marketing.pages.comparatif.hero.eyebrow"), path: "/tarifs/comparatif" },
    ]),
  ];

  async function onSubmitEmail(e: React.FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    const trimmed = email.trim();
    if (!trimmed || !EMAIL_RE.test(trimmed) || trimmed.length > 254) {
      toast.error(t("marketing.pages.comparatif.hero.emailInvalid"));
      return;
    }
    setStatus("submitting");
    try {
      const { error } = await supabase.functions.invoke("marketing-send-comparison", {
        body: { email: trimmed, locale },
      });
      if (error) {
        // Supabase wraps non-2xx as an error; differentiate rate-limit
        // (429) so the user gets a clearer message.
        const msg = (error as { message?: string }).message ?? "";
        if (msg.includes("429") || /rate/i.test(msg)) {
          toast.error(t("marketing.pages.comparatif.hero.emailRateLimited"));
        } else {
          toast.error(t("marketing.pages.comparatif.hero.emailError"));
        }
        setStatus("idle");
        return;
      }
      setStatus("ok");
      setEmail("");
      toast.success(t("marketing.pages.comparatif.hero.emailSuccess"));
    } catch {
      toast.error(t("marketing.pages.comparatif.hero.emailError"));
      setStatus("idle");
    }
  }

  return (
    <>
      <SeoHead
        title={t("marketing.pages.comparatif.seo.title")}
        description={t("marketing.pages.comparatif.seo.description")}
        path="/tarifs/comparatif"
        locale={locale}
        jsonLd={jsonLd}
      />

      {/* Print stylesheet — scoped via id so it only fires from this page. */}
      <style>{`
        @media print {
          #marketing-comparison-print-root nav,
          #marketing-comparison-print-root footer,
          #marketing-comparison-print-root [data-print-hide="true"] {
            display: none !important;
          }
          #marketing-comparison-print-root {
            background: #ffffff !important;
            color: #000000 !important;
          }
          #marketing-comparison-print-root table {
            page-break-inside: auto;
            width: 100% !important;
            font-size: 10pt;
          }
          #marketing-comparison-print-root thead { display: table-header-group; }
          #marketing-comparison-print-root tr { page-break-inside: avoid; page-break-after: auto; }
          #marketing-comparison-print-root .text-muted-foreground { color: #444 !important; }
          #marketing-comparison-print-root .border-rule { border-color: #888 !important; }
          @page { margin: 12mm; size: A4 landscape; }
        }
      `}</style>

      <div id="marketing-comparison-print-root">
        <Section padding="lg">
          <div className="max-w-3xl">
            <Eyebrow>{t("marketing.pages.comparatif.hero.eyebrow")}</Eyebrow>
            <DisplayHeading level={1} className="mt-4">
              {t("marketing.pages.comparatif.hero.title")}
            </DisplayHeading>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
              {t("marketing.pages.comparatif.hero.lead")}
            </p>
          </div>

          <div
            className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
            data-print-hide="true"
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => window.print()}
              className="self-start"
            >
              <Printer className="mr-2 h-4 w-4" aria-hidden />
              {t("marketing.pages.comparatif.hero.printCta")}
            </Button>

            <form onSubmit={onSubmitEmail} className="flex w-full max-w-md flex-col gap-2">
              <Label htmlFor="comparison-email" className="dateline">
                {t("marketing.pages.comparatif.hero.emailLabel")}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="comparison-email"
                  type="email"
                  required
                  inputMode="email"
                  autoComplete="email"
                  placeholder={t("marketing.pages.comparatif.hero.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === "submitting"}
                />
                <Button type="submit" disabled={status === "submitting" || status === "ok"}>
                  {status === "submitting"
                    ? t("marketing.pages.comparatif.hero.emailSubmitting")
                    : t("marketing.pages.comparatif.hero.emailSubmit")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t("marketing.pages.comparatif.hero.emailConsent")}</p>
            </form>
          </div>
        </Section>

        <Section padding="md">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-foreground">
                  <th className="px-3 py-3 text-left font-medium">
                    {t("marketing.pages.comparatif.headers.feature")}
                  </th>
                  {PLAN_SLUGS.map((slug) => (
                    <th key={slug} className="px-3 py-3 text-left font-medium">
                      {locText(PLAN_DISPLAY_NAMES[slug], locale)}
                    </th>
                  ))}
                </tr>
                <tr className="border-b border-rule">
                  <td className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("marketing.pages.comparatif.headers.monthlyPrice")}
                  </td>
                  {PLAN_SLUGS.map((slug) => (
                    <td key={slug} className="px-3 py-2 text-sm tabular-nums">
                      {formatPriceOrCustom(planMonthlyCents(slug), locale)}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PLAN_MATRIX.map((cat) => (
                  <CategoryRows key={cat.id} category={cat} locale={locale} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between" data-print-hide="true">
            <Link to="/tarifs" className="inline-flex items-center gap-2 text-sm font-medium text-accent-press hover:underline">
              <ArrowRight className="h-4 w-4 rotate-180" aria-hidden />
              {t("marketing.header.nav.pricing")}
            </Link>
            <Link to="/contact?subject=enterprise" className="inline-flex items-center gap-2 text-sm font-medium text-accent-press hover:underline">
              {t("marketing.pages.tarifs.plans.ctaEnterprise")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </Section>
      </div>
    </>
  );
}

function CategoryRows({ category, locale }: { category: typeof PLAN_MATRIX[number]; locale: Locale }) {
  return (
    <>
      <tr className="bg-muted/30">
        <th
          colSpan={PLAN_SLUGS.length + 1}
          scope="colgroup"
          className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {locText(category.label, locale)}
        </th>
      </tr>
      {category.rows.map((row) => (
        <tr key={row.id} className="border-b border-rule/50">
          <th scope="row" className="px-3 py-3 text-left font-normal text-foreground">
            {locText(row.label, locale)}
            {row.hint && <div className="mt-1 text-xs text-muted-foreground">{locText(row.hint, locale)}</div>}
          </th>
          {PLAN_SLUGS.map((slug) => {
            const cell = row.cells[slug];
            if (cell.kind === "boolean") {
              return (
                <td key={slug} className="px-3 py-3">
                  {cell.value ? (
                    <Check className="h-4 w-4 text-emerald-600" aria-label="Oui" />
                  ) : (
                    <X className="h-4 w-4 text-muted-foreground/50" aria-label="Non" />
                  )}
                </td>
              );
            }
            return (
              <td key={slug} className="px-3 py-3 text-foreground/90">
                {formatCell(cell, locale)}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
